<template>
  <div
    ref="container"
    class="popover-container"
  >
    <slot>
      <i
        class="fa fa-question-circle"
        aria-hidden="true"
      />
    </slot>
    <div
      :style="{ color: color, backgroundColor: bgcolor, fontSize: textsize + 'px', width: width + 'px', textAlign: textAlign }"
      :class="['vtooltip', always ? 'vtooltip-always' : '', 'vtooltip-' + positionComputed]"
      class="selectable"
      @click="($event) => { $event.stopPropagation(); $event.preventDefault(); }"
      @blur="hideHint()"
    >
      {{ text.trim() }}
    </div>
  </div>
</template>

<script>
import { debounce } from 'lodash'

export default {
  props: {
    always: {
      type: Boolean,
      default: false,
    },
    text: {
      type: String,
      required: true,
    },
    color: {
      type: String,
      default: '#0C3A58',
    },
    bgcolor: {
      type: String,
      default: '#f0f0f0',
    },
    textsize: {
      type: Number,
      default: 14,
    },
    width: {
      type: Number,
      default: 400,
    },
    textAlign: {
      type: String,
    },
    position: {
      type: String,
      validator (value) {
        return ['top-left', 'top-right', 'bottom-left', 'bottom-right'].includes(value)
      },
      default: 'top-left',
    },
  },
  data () {
    return {
      positionComputed: this.position,
      onResizeDebounced: debounce(this.onResize, 400),
    }
  },
  mounted () {
    this.onResize()
    window.addEventListener('resize', this.onResizeDebounced)
  },
  beforeDestroy () {
    window.removeEventListener('resize', this.onResizeDebounced)
  },
  methods: {
    clickToPin (event) {
      this.always = !this.always
      event.preventDefault()
      event.stopPropagation()
    },
    hideHint () {
      this.always = false
    },
    onResize () {
      const { left, right } = this.$refs.container.getBoundingClientRect()
      const viewPortWidth = document.documentElement.offsetWidth

      switch (this.position) {
        case 'top-left':
          this.positionComputed = left >= this.width ? 'top-left' : 'top-right'
          break
        case 'top-right':
          this.positionComputed = (viewPortWidth - right) >= this.width ? 'top-right' : 'top-left'
          break
        case 'bottom-left':
          this.positionComputed = left >= this.width ? 'bottom-left' : 'bottom-right'
          break
        case 'bottom-right':
          this.positionComputed = (viewPortWidth - right) >= this.width ? 'bottom-right' : 'bottom-left'
          break
      }
    },
  },
}
</script>

<style lang="scss" scoped>
$border-width: 8px;
$border-color: #0c3a58;

.selectable {
  -webkit-user-select: auto;
  -moz-user-select: auto;
  -ms-user-select: auto;
  user-select: auto;
}
.popover-container {
  position: relative;
  display: inline;
}
.popover-container:hover > .vtooltip {
  visibility: visible;
}
.vtooltip-always {
  visibility: visible !important;
}
.vtooltip {
  margin: 0;
  border: 0;
  font-size: 100%;
  font: inherit;
  vertical-align: baseline;
  position: absolute;
  padding: 8px 10px;
  line-height: 20px;
  word-wrap: break-word;
  white-space: pre-wrap;
  box-shadow: 4px 4px 8px rgba(0, 0, 0, 0.3);
  border: 1px solid $border-color;
  z-index: 1000;
  visibility: hidden;
  top: -8px;
  right: 0;
  transform: translateX(0) translateY(-100%);
}
.vtooltip i {
  position: absolute;
  background: 0 0;
  border: 8px solid transparent;
  z-index: 1001;
  left: calc(98% - 8px);
}

.vtooltip-top-left {
  top: -8px;
  right: 0;
  transform: translateX(0) translateY(-100%);
}
.vtooltip-top-right {
  top: -8px;
  left: 0;
  transform: translateX(0) translateY(-100%);
}
.vtooltip-bottom-left {
  top: auto;
  left: auto;
  bottom: -8px;
  right: 0;
  transform: translateX(0) translateY(100%);
}
.vtooltip-bottom-right {
  top: auto;
  right: auto;
  bottom: -8px;
  left: 0;
  transform: translateX(0) translateY(100%);
}

.vtooltip-rounded {
  border-radius: 10px;
}

.vtooltip-top-left i,
.vtooltip-top-right i {
  top: auto;
  border-bottom: none;
  bottom: -8px;
}
.vtooltip-top-right i {
  left: calc(2% - 8px);
}
.vtooltip-bottom-left i,
.vtooltip-bottom-right i {
  border-top: none;
  top: -8px;
}
.vtooltip-bottom-right i {
  left: calc(2% - 8px);
}

.vtooltip::before,
.vtooltip::after {
  content: "";
  position: absolute;
  width: 0;
  height: 0;
  border-left: $border-width solid transparent;
  border-right: $border-width solid transparent;
  top: 100%;
  right: 0;
}

.vtooltip-bottom-left::before,
.vtooltip-bottom-left::after {
  top: -$border-width;
  left: auto;
  right: 0;
}

.vtooltip-bottom-left::before {
  top: -9px;
}

.vtooltip-top-right::before,
.vtooltip-top-right::after {
  left: 0;
  right: auto;
}
.vtooltip-bottom-right::before,
.vtooltip-bottom-right::after {
  top: -$border-width;
  left: 0;
  right: auto;
}
.vtooltip-bottom-right::before {
  top: -9px;
}

.vtooltip-top-left::before,
.vtooltip-top-right::before {
  border-top: $border-width solid $border-color;
  margin-top: 1px;
}

.vtooltip-bottom-left::before,
.vtooltip-bottom-right::before {
  border-bottom: $border-width solid $border-color;
  margin-bottom: 1px;
}
/* The white fill of the triangle */
.vtooltip-top-left::after,
.vtooltip-top-right::after {
  border-top: $border-width solid #f0f0f0;
  z-index: 1;
}
.vtooltip-bottom-left::after,
.vtooltip-bottom-right::after {
  border-bottom: $border-width solid #f0f0f0;
  z-index: 1;
}
</style>
